import React, { useEffect, useMemo, useState } from 'react';
import type { AssignmentCoverImage, AssignmentPointsNoteDoc } from '../../types/assignmentPointsNote';
import {
  listAssignmentMemoLinkCandidates,
  type AssignmentMemoLinkCandidate,
  type AssignmentMemoMemberRef,
} from '../../utils/classRecordMemosAssignmentNotes';
import { getAssignmentPointsNote } from '../../services/assignmentPointsNoteService';
import { renderNoteWithMemberTags } from '../../utils/classRecordMemoHashtags';
import type { ClassMemberGradeLayout, ClassMemberRow } from '../../utils/classMembersStorage';
import { firstRecordGroupId } from '../../utils/classMemberRecordGroupUtils';
import { parseISODate } from '../../utils/date';
import CachedAssignmentCoverImage from '../common/CachedAssignmentCoverImage';
import RecordMemberGroupSelect from '../program/RecordMemberGroupSelect';

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'] as const;

function formatDueDate(dueDate: string): string {
  if (!dueDate) return '';
  const d = parseISODate(dueDate);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()} (${DAY_LABELS[d.getDay()]})`;
}

function membersForTagRender(membersById: Map<string, AssignmentMemoMemberRef>): ClassMemberRow[] {
  return Array.from(membersById.values()).map((m) => ({
    id: m.id,
    name: m.name,
    gender: '',
    groupNo: null,
    entryNumber: '',
  }));
}

const AssignmentMemoLinkPickerModal: React.FC<{
  open: boolean;
  workspaceId: string;
  gradeId: string;
  membersById: Map<string, AssignmentMemoMemberRef>;
  memberLayout: ClassMemberGradeLayout;
  /** 이미 로드된 문서가 있으면 재사용 */
  doc?: AssignmentPointsNoteDoc | null;
  /** 모달 열 때 기본으로 고를 그룹 */
  initialGroupId?: string | null;
  onClose: () => void;
  onPick: (candidate: AssignmentMemoLinkCandidate) => void;
}> = ({
  open,
  workspaceId,
  gradeId,
  membersById,
  memberLayout,
  doc: docProp,
  initialGroupId,
  onClose,
  onPick,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doc, setDoc] = useState<AssignmentPointsNoteDoc | null>(docProp ?? null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [groupId, setGroupId] = useState<string | null>(null);

  const hasGroups = memberLayout.groups.length > 0;

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setExpandedId(null);
    setError(null);
    const initial = (initialGroupId ?? '').trim();
    if (initial && memberLayout.groups.some((g) => g.id === initial)) {
      setGroupId(initial);
    } else {
      setGroupId(firstRecordGroupId(memberLayout));
    }
    if (docProp) {
      setDoc(docProp);
      setLoading(false);
      return;
    }
    const ws = workspaceId.trim();
    const gid = gradeId.trim();
    if (!ws || !gid) {
      setDoc(null);
      setError('클래스를 선택해 주세요.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void getAssignmentPointsNote(ws, gid)
      .then((next) => {
        if (cancelled) return;
        setDoc(next);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : '과제 기록을 불러오지 못했습니다.');
        setDoc(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, gradeId, docProp, initialGroupId, memberLayout]);

  const groupLabel = useMemo(() => {
    const id = (groupId ?? '').trim();
    if (!id) return '';
    const hit = memberLayout.groups.find((g) => g.id === id);
    return (hit?.name ?? '').trim() || id;
  }, [groupId, memberLayout.groups]);

  const allowedMemberIds = useMemo(() => {
    const id = (groupId ?? '').trim();
    if (!id) return null;
    return new Set((memberLayout.membersByGroupId[id] ?? []).map((m) => m.id));
  }, [groupId, memberLayout.membersByGroupId]);

  const candidates = useMemo(
    () =>
      listAssignmentMemoLinkCandidates(doc, membersById, {
        allowedMemberIds,
        groupId,
        groupLabel,
      }),
    [allowedMemberIds, doc, groupId, groupLabel, membersById]
  );

  const coverImageById = useMemo(() => {
    const map = new Map<string, AssignmentCoverImage>();
    for (const img of doc?.coverImages ?? []) {
      map.set(img.id, img);
    }
    return map;
  }, [doc?.coverImages]);

  const tagMembers = useMemo(() => {
    if (!allowedMemberIds) return membersForTagRender(membersById);
    const filtered = new Map<string, AssignmentMemoMemberRef>();
    for (const [id, m] of Array.from(membersById.entries())) {
      if (allowedMemberIds.has(id)) filtered.set(id, m);
    }
    return membersForTagRender(filtered);
  }, [allowedMemberIds, membersById]);

  const selected = useMemo(
    () => candidates.find((c) => c.itemId === selectedId) ?? null,
    [candidates, selectedId]
  );

  const groupRequiredMissing = hasGroups && !(groupId ?? '').trim();

  if (!open) return null;

  return (
    <div
      className="cp-modal-backdrop cp-assignment-memo-link-picker-backdrop"
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="cp-modal cp-assignment-memo-link-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-assignment-memo-link-picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="cp-assignment-memo-link-picker-title">과제 기록 가져오기</h3>
        {hasGroups ? (
          <div className="cp-assignment-memo-link-picker-group">
            <RecordMemberGroupSelect
              id="cp-assignment-memo-link-picker-group"
              layout={memberLayout}
              value={groupId}
              requireGroup
              clearOptionLabel="학급 선택"
              onChange={(next) => {
                setGroupId(next);
                setSelectedId(null);
                setExpandedId(null);
              }}
            />
          </div>
        ) : null}
        <div className="cp-modal-body cp-assignment-memo-link-picker-body">
          {loading ? <p role="status">불러오는 중…</p> : null}
          {error ? <p className="cp-admin-learning-error">{error}</p> : null}
          {!loading && !error && groupRequiredMissing ? (
            <p role="status">학급(멤버 그룹)을 먼저 선택해 주세요.</p>
          ) : null}
          {!loading && !error && !groupRequiredMissing && candidates.length === 0 ? (
            <p role="status">
              {hasGroups
                ? '선택한 학급에 메모가 있는 일반·프로젝트 과제가 없습니다.'
                : '메모가 있는 일반·프로젝트 과제가 없습니다.'}
            </p>
          ) : null}
          {!loading && !groupRequiredMissing && candidates.length > 0 ? (
            <ul className="cp-assignment-memo-link-picker-list">
              {candidates.map((c) => {
                const isSelected = selectedId === c.itemId;
                const expanded = expandedId === c.itemId;
                const due = formatDueDate(c.dueDate);
                const coverImage = c.coverImageId ? coverImageById.get(c.coverImageId) : undefined;
                const pointsLabel =
                  c.kind === 'project' ? `합계 ${c.basePoints}점` : `기본포인트 ${c.basePoints}`;
                return (
                  <li
                    key={c.itemId}
                    className={
                      'cp-assignment-memo-link-picker-block-wrap' + (expanded ? ' is-expanded' : '')
                    }
                  >
                    <div
                      className={
                        'cp-apn-block cp-assignment-memo-link-picker-apn-block' +
                        (isSelected ? ' is-selected' : '')
                      }
                    >
                      <button
                        type="button"
                        className="cp-apn-block-main"
                        aria-expanded={expanded}
                        onClick={() => {
                          setSelectedId(c.itemId);
                          setExpandedId((prev) => (prev === c.itemId ? null : c.itemId));
                        }}
                      >
                        <span className="cp-apn-block-icon-slot">
                          <span
                            className={`cp-apn-block-icon cp-apn-block-icon--${c.kind}`}
                            aria-hidden
                          >
                            {coverImage ? (
                              <CachedAssignmentCoverImage
                                image={coverImage}
                                className="cp-apn-block-icon-img"
                                fallback={<span className="cp-apn-block-icon-placeholder" />}
                              />
                            ) : (
                              <span className="cp-apn-block-icon-placeholder" />
                            )}
                          </span>
                        </span>
                        <span className="cp-apn-block-body">
                          <span className="cp-apn-block-head-row">
                            <span
                              className="cp-apn-block-badge"
                              style={{ background: c.categoryColor }}
                            >
                              {c.categoryLabel || (c.kind === 'project' ? '프로젝트' : '기본')}
                            </span>
                            {c.groupLabel ? (
                              <span className="cp-apn-block-shared-badge">{c.groupLabel}</span>
                            ) : null}
                          </span>
                          <span className="cp-apn-block-title-row">
                            <span className="cp-apn-block-title">{c.title}</span>
                          </span>
                          <span className="cp-apn-block-meta">
                            {due ? <span className="cp-apn-block-meta-due">{due}</span> : null}
                            <span className="cp-apn-block-meta-secondary">
                              <span className="cp-apn-block-meta-points">{pointsLabel}</span>
                            </span>
                          </span>
                          <span
                            className={
                              'cp-apn-block-status' + (c.memoLineCount > 0 ? ' has-pending' : ' is-done')
                            }
                          >
                            {c.memoLineCount > 0 ? `메모 ${c.memoLineCount}명` : '메모 없음'}
                          </span>
                        </span>
                      </button>
                    </div>
                    {expanded ? (
                      <div className="cp-assignment-memo-link-picker-block-memos" role="region">
                        {renderNoteWithMemberTags(c.memoNote, tagMembers, {
                          applyBulkMemberTagRules: false,
                        })}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            disabled={!selected || groupRequiredMissing}
            onClick={() => {
              if (!selected || groupRequiredMissing) return;
              onPick(selected);
            }}
          >
            적용
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignmentMemoLinkPickerModal;
